import "@/App.css";
import { lazy, Suspense, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useSearchParams } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/lib/auth";
import { isStaff } from "@/lib/roles";
import OperatorLanding from "@/pages/OperatorLanding";
import CampaignLanding from "@/pages/CampaignLanding";
import NotFound from "@/pages/NotFound";
import QrRedirect from "@/pages/QrRedirect";
import CookieBanner from "@/components/CookieBanner";
import RouteMeta from "@/components/RouteMeta";

// Every other page is its own chunk so the public pages (/, /<campaign>, QR redirects) ship a small bundle.
// A chunk 404 after a redeploy (stale hashes) reloads the page once instead of a white screen.
const page = (loader) => lazy(() => loader().catch((e) => {
  if (!sessionStorage.getItem("chunk-reload")) { sessionStorage.setItem("chunk-reload", "1"); window.location.reload(); }
  throw e;
}));
const Login = page(() => import("@/pages/Login"));
const Dashboard = page(() => import("@/pages/Dashboard"));
const Landing = page(() => import("@/pages/Landing"));
const Pricing = page(() => import("@/pages/Pricing"));
const About = page(() => import("@/pages/About"));
const States = page(() => import("@/pages/States"));
const StatePage = page(() => import("@/pages/StatePage"));
const SubscriberPortal = page(() => import("@/pages/SubscriberPortal"));
const PayQuote = page(() => import("@/pages/PayQuote"));
const Campaigns = page(() => import("@/pages/Campaigns"));
const PortalSuccess = page(() => import("@/pages/PortalSuccess"));
const PortalCampaigns = page(() => import("@/pages/PortalCampaigns"));
const FortuneLanding = page(() => import("@/pages/FortuneLanding"));
const FortuneSignup = page(() => import("@/pages/FortuneSignup"));
const Buy = page(() => import("@/pages/Buy"));
const BuyProduct = page(() => import("@/pages/BuyProduct"));
const BuyThankYou = page(() => import("@/pages/BuyThankYou"));
const Blog = page(() => import("@/pages/Blog"));
const BlogPost = page(() => import("@/pages/BlogPost"));
const BlogAdmin = page(() => import("@/pages/BlogAdmin"));
const Sponsor = page(() => import("@/pages/Sponsor"));
const Engagement = page(() => import("@/pages/Engagement"));
const FortuneSocial = page(() => import("@/pages/FortuneSocial"));
const FortuneInquiries = page(() => import("@/pages/FortuneInquiries"));
const FortuneMap = page(() => import("@/pages/FortuneMap"));
const FortuneBroadcast = page(() => import("@/pages/FortuneBroadcast"));
const FortuneFlyerEditor = page(() => import("@/pages/FortuneFlyerEditor"));
const Privacy = page(() => import("@/pages/Privacy"));
const Terms = page(() => import("@/pages/Terms"));
const ArcadeSocial = page(() => import("@/pages/ArcadeSocial"));
const SmsPage = page(() => import("@/pages/SmsPage"));
const EmailTool = page(() => import("@/pages/EmailTool"));
const ConsentAudit = page(() => import("@/pages/ConsentAudit"));
const DataQuality = page(() => import("@/pages/DataQuality"));
const UserAudit = page(() => import("@/pages/UserAudit"));
const ArcadeOpsBroadcast = page(() => import("@/pages/ArcadeOpsBroadcast"));
const BetaInvites = page(() => import("@/pages/BetaInvites"));
const EmailClicks = page(() => import("@/pages/EmailClicks"));
const CampaignNew = page(() => import("@/pages/CampaignNew"));
const Social = page(() => import("@/pages/Social"));
const CompanyInfo = page(() => import("@/pages/CompanyInfo"));
const Redeem = page(() => import("@/pages/Redeem"));
const SmsConsent = page(() => import("@/pages/SmsConsent"));
const AdsFortune = page(() => import("@/pages/AdsFortune"));
const AdsCenter = page(() => import("@/pages/AdsCenter"));
const Prospects = page(() => import("@/pages/Prospects"));
const CallKit = page(() => import("@/pages/CallKit"));
// Blitz page retired 2026-09-03 — merged into Dialer. /app/blitz redirects to /app/dial.
const Dialer = page(() => import("@/pages/Dialer"));
const ImportLeads = page(() => import("@/pages/ImportLeads"));
const AiControl = page(() => import("@/pages/AiControl"));
const KillSwitches = page(() => import("@/pages/KillSwitches"));
const LuckyControl = page(() => import("@/pages/LuckyControl"));
const Deliverability = page(() => import("@/pages/Deliverability"));
const OutreachBatches = page(() => import("@/pages/OutreachBatches"));
const DailySend = page(() => import("@/pages/DailySend"));
const ClickFollowups = page(() => import("@/pages/ClickFollowups"));
const RulesPage = page(() => import("@/pages/RulesPage"));
const PipelineOverview = page(() => import("@/pages/PipelineOverview"));
const JobbersPage = page(() => import("@/pages/JobbersPage"));
const OutreachBlasts = page(() => import("@/pages/OutreachBlasts"));
const BrandAssets = page(() => import("@/pages/BrandAssets"));
const Demo = page(() => import("@/pages/Demo"));
const MessagingKit = page(() => import("@/pages/MessagingKit"));
const SiteAnalytics = page(() => import("@/pages/SiteAnalytics"));
const AppShell = page(() => import("@/components/AppShell"));
const SupportWidget = page(() => import("@/components/SupportWidget"));
const VerifyEmail = page(() => import("@/pages/VerifyEmail"));
const ResetPassword = page(() => import("@/pages/ResetPassword"));
const CancelTrial = page(() => import("@/pages/CancelTrial"));
const Signups = page(() => import("@/pages/Signups"));
const CampaignRequests = page(() => import("@/pages/CampaignRequests"));

// Route-chunk fallback: a plain page-coloured screen (no "LOADING…" text) so the hand-off to the page is a fade, not a flash.
function Loading() {
  return <div className="min-h-screen bg-background" aria-busy="true" data-testid="route-loading" />;
}

// Warm the public chunks right after the first paint so /saas, /pricing and /fortune open without a blank frame.
function PrefetchPublicChunks() {
  useEffect(() => {
    const warm = () => { import("@/pages/Landing"); import("@/pages/Pricing"); import("@/pages/FortuneSignup"); import("@/pages/Login"); };
    const idle = window.requestIdleCallback || ((fn) => setTimeout(fn, 1200));
    const id = idle(warm);
    return () => (window.cancelIdleCallback || clearTimeout)(id);
  }, []);
  return null;
}

function Protected({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <Loading />;
  // Keep the deep link (e.g. /portal?pay=<id> from the quote email) so login can land back on it and resume.
  if (!user) return <Navigate to={`/login?next=${encodeURIComponent(location.pathname + location.search)}`} replace />;
  return children;
}

// AdminOnly wraps operator routes (Dashboard, Fortune tools, SMS admin, etc.).
// Subscribers who navigate here are bounced to /portal — the only place they
// can pay for state access. Prevents "sign up as subscriber and just navigate
// to /app to skip payment" leaks.
function AdminOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <Loading />;
  if (!user) return <Navigate to="/login" replace />;
  if (!isStaff(user)) return <Navigate to="/portal" replace />;
  return children;
}

// Kill switches / automatic-outreach controls are super-admin territory; the ops admin is sent back to Leads.
function NoOpsAdmin({ children }) {
  const { user } = useAuth();
  if (user?.role === "ops_admin") return <Navigate to="/app" replace />;
  return children;
}

// Renders the floating support widget only when a user is signed in. Kept
// inside AuthProvider so it can react to auth state changes without a
// full-page remount.
function SupportWidgetHost() {
  const { user } = useAuth();
  if (!user) return null;
  return <Suspense fallback={null}><SupportWidget user={user} /></Suspense>;
}

// /portal: subscribers get a clean, self-contained page (states · leads ·
// billing — the portal has its own header). Admins keep the operator shell
// so they can hop back to the rest of the app.
function PortalShell({ children }) {
  const { user } = useAuth();
  if (isStaff(user)) return <AppShell>{children}</AppShell>;
  return <div data-testid="subscriber-shell">{children}</div>;
}

// /portal?pay=<id> is the public "Pay & Approve" link from the quote email — no login, so it must be
// resolved BEFORE <Protected> gets a chance to bounce a logged-out payer to /login.
function PortalEntry() {
  const [params] = useSearchParams();
  if (params.get("pay")) return <PayQuote />;
  return (
    <Protected>
      <PortalShell>
        <SubscriberPortal />
      </PortalShell>
    </Protected>
  );
}

export default function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <Toaster theme="dark" position="top-right" richColors closeButton />
          <CookieBanner />
          <RouteMeta />
          <PrefetchPublicChunks />
          <Suspense fallback={<Loading />}>
          <Routes>
            <Route path="/" element={<OperatorLanding />} />
            <Route path="/fleet" element={<OperatorLanding fleetMode />} />
            <Route path="/c/:slug" element={<CampaignLanding />} />
            <Route path="/preview/:slug" element={<CampaignLanding />} />
            <Route path="/saas" element={<Landing />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/about" element={<About />} />
            <Route path="/states" element={<States />} />
            <Route path="/states/:slug" element={<StatePage />} />
            {/* Case study hidden until a real, named customer signs off on it. */}
            <Route path="/case-study" element={<Navigate to="/pricing" replace />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/social" element={<ArcadeSocial />} />
            {/* Public Fortune Games quote page (homepage card + QR land here). No AppShell — visitors must never hit /login. */}
            <Route path="/fortune" element={<FortuneSignup />} />
            {/* Public Buy pages (Oct 19): a rep texts /buy/<slug> after a call; Stripe Checkout, no sign-in. */}
            <Route path="/buy" element={<Buy />} />
            <Route path="/buy/thank-you" element={<BuyThankYou />} />
            <Route path="/buy/:slug" element={<BuyProduct />} />
            <Route path="/fortune/classic" element={<FortuneLanding />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route path="/sponsor" element={<Sponsor />} />
            {/* AOG sign-up now lives on the AOG Coin campaign page; old QR codes / flyers / links to /aog keep working. */}
            <Route path="/aog" element={<Navigate to="/aog-coin" replace />} />
            <Route path="/demo" element={<Demo />} />
            <Route path="/fortune/social" element={<AppShell><FortuneSocial /></AppShell>} />
            <Route
              path="/fortune/inquiries"
              element={
                <AdminOnly>
                  <AppShell><FortuneInquiries /></AppShell>
                </AdminOnly>
              }
            />
            <Route
              path="/fortune/map"
              element={
                <AdminOnly>
                  <FortuneMap />
                </AdminOnly>
              }
            />
            <Route
              path="/fortune/broadcast"
              element={
                <AdminOnly>
                  <AppShell><FortuneBroadcast /></AppShell>
                </AdminOnly>
              }
            />
            <Route
              path="/fortune/flyer"
              element={
                <AdminOnly>
                  <AppShell><FortuneFlyerEditor /></AppShell>
                </AdminOnly>
              }
            />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Login />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route
              path="/app"
              element={
                <AdminOnly>
                  <AppShell><Dashboard /></AppShell>
                </AdminOnly>
              }
            />
            <Route
              path="/app/email"
              element={
                <AdminOnly>
                  <AppShell><EmailTool /></AppShell>
                </AdminOnly>
              }
            />
            <Route
              path="/app/sms"
              element={
                <AdminOnly>
                  <AppShell><SmsPage /></AppShell>
                </AdminOnly>
              }
            />
            <Route path="/app/sms-preview" element={<Navigate to="/app/sms" replace />} />
            <Route
              path="/app/pipeline"
              element={
                <AdminOnly>
                  <AppShell><PipelineOverview /></AppShell>
                </AdminOnly>
              }
            />
            <Route
              path="/app/rules"
              element={
                <AdminOnly>
                  <AppShell><RulesPage /></AppShell>
                </AdminOnly>
              }
            />
            <Route path="/app/call-queue" element={<Navigate to="/app/dial?tab=queue" replace />} />
            <Route path="/app/pitch-library" element={<Navigate to="/app/dial?tab=pitch-library" replace />} />
            <Route
              path="/app/jobbers"
              element={
                <AdminOnly>
                  <AppShell><JobbersPage /></AppShell>
                </AdminOnly>
              }
            />
            <Route
              path="/app/consent-audit"
              element={
                <AdminOnly>
                  <AppShell><ConsentAudit /></AppShell>
                </AdminOnly>
              }
            />
            <Route path="/app/voicemails" element={<Navigate to="/app/pipeline?tab=outreach&sub=calling" replace />} />
            <Route path="/app/pulse" element={<Navigate to="/app/pipeline?tab=outreach&sub=sms" replace />} />
            <Route
              path="/app/social"
              element={
                <AdminOnly>
                  <AppShell><Social /></AppShell>
                </AdminOnly>
              }
            />
            <Route path="/app/social/autopilot" element={<Navigate to="/app/social" replace />} />
            <Route path="/app/social/command" element={<Navigate to="/app/social" replace />} />
            <Route
              path="/app/campaigns"
              element={
                <AdminOnly>
                  <AppShell><Campaigns /></AppShell>
                </AdminOnly>
              }
            />
            <Route
              path="/app/brand-assets"
              element={
                <AdminOnly>
                  <AppShell><BrandAssets /></AppShell>
                </AdminOnly>
              }
            />
            <Route
              path="/app/messaging-kit"
              element={
                <AdminOnly>
                  <AppShell><MessagingKit /></AppShell>
                </AdminOnly>
              }
            />
            <Route
              path="/app/site-analytics"
              element={
                <AdminOnly>
                  <AppShell><SiteAnalytics /></AppShell>
                </AdminOnly>
              }
            />
            <Route path="/app/replies" element={<Navigate to="/app/pipeline?tab=outreach" replace />} />
            <Route
              path="/app/data-quality"
              element={
                <AdminOnly>
                  <AppShell><DataQuality /></AppShell>
                </AdminOnly>
              }
            />
            <Route
              path="/app/user-audit"
              element={
                <AdminOnly>
                  <AppShell><UserAudit /></AppShell>
                </AdminOnly>
              }
            />
            <Route
              path="/app/signups"
              element={
                <AdminOnly>
                  <AppShell><Signups /></AppShell>
                </AdminOnly>
              }
            />
            <Route
              path="/app/campaign-requests"
              element={
                <AdminOnly>
                  <AppShell><CampaignRequests /></AppShell>
                </AdminOnly>
              }
            />
            <Route
              path="/app/broadcast"
              element={
                <AdminOnly>
                  <AppShell><ArcadeOpsBroadcast /></AppShell>
                </AdminOnly>
              }
            />
            <Route
              path="/app/beta-invites"
              element={
                <AdminOnly>
                  <AppShell><BetaInvites /></AppShell>
                </AdminOnly>
              }
            />
            <Route
              path="/app/email-clicks"
              element={
                <AdminOnly>
                  <AppShell><EmailClicks /></AppShell>
                </AdminOnly>
              }
            />
            <Route
              path="/app/campaigns/new"
              element={
                <AdminOnly>
                  <AppShell><CampaignNew /></AppShell>
                </AdminOnly>
              }
            />
            <Route
              path="/app/company-info"
              element={
                <AdminOnly>
                  <AppShell><CompanyInfo /></AppShell>
                </AdminOnly>
              }
            />
            <Route path="/redeem" element={<Redeem />} />
            <Route path="/sms-consent" element={<SmsConsent />} />
            <Route path="/ads/fortune" element={<AdsFortune />} />
            <Route
              path="/app/ads"
              element={
                <AdminOnly>
                  <AppShell><AdsCenter /></AppShell>
                </AdminOnly>
              }
            />
            <Route
              path="/app/prospects"
              element={
                <AdminOnly>
                  <AppShell><Prospects /></AppShell>
                </AdminOnly>
              }
            />
            <Route
              path="/app/call-kit"
              element={
                <AdminOnly>
                  <AppShell><CallKit /></AppShell>
                </AdminOnly>
              }
            />
            <Route path="/app/blitz" element={<Navigate to="/app/dial" replace />} />
            <Route
              path="/app/dial"
              element={
                <AdminOnly>
                  <AppShell><Dialer /></AppShell>
                </AdminOnly>
              }
            />
            <Route
              path="/app/import"
              element={
                <AdminOnly>
                  <AppShell><ImportLeads /></AppShell>
                </AdminOnly>
              }
            />
            <Route
              path="/app/ai-control"
              element={
                <AdminOnly>
                  <AppShell><AiControl /></AppShell>
                </AdminOnly>
              }
            />
            <Route
              path="/app/kill-switches"
              element={
                <AdminOnly>
                  <NoOpsAdmin><AppShell><KillSwitches /></AppShell></NoOpsAdmin>
                </AdminOnly>
              }
            />
            <Route
              path="/app/lucky"
              element={
                <AdminOnly>
                  <AppShell><LuckyControl /></AppShell>
                </AdminOnly>
              }
            />
            <Route
              path="/app/daily-send"
              element={
                <AdminOnly>
                  <AppShell><DailySend /></AppShell>
                </AdminOnly>
              }
            />
            <Route
              path="/app/deliverability"
              element={
                <AdminOnly>
                  <AppShell><Deliverability /></AppShell>
                </AdminOnly>
              }
            />
            <Route
              path="/app/outreach-batches"
              element={
                <AdminOnly>
                  <AppShell><OutreachBatches /></AppShell>
                </AdminOnly>
              }
            />
            <Route
              path="/app/outreach-batches/:id"
              element={
                <AdminOnly>
                  <AppShell><OutreachBatches /></AppShell>
                </AdminOnly>
              }
            />
            <Route
              path="/app/click-followups"
              element={
                <AdminOnly>
                  <AppShell><ClickFollowups /></AppShell>
                </AdminOnly>
              }
            />
            <Route
              path="/app/blog"
              element={
                <AdminOnly>
                  <AppShell><BlogAdmin /></AppShell>
                </AdminOnly>
              }
            />
            <Route
              path="/app/outreach-blasts"
              element={
                <AdminOnly>
                  <AppShell><OutreachBlasts /></AppShell>
                </AdminOnly>
              }
            />
            <Route
              path="/app/engagement"
              element={
                <AdminOnly>
                  <Engagement />
                </AdminOnly>
              }
            />
            <Route path="/r/:code" element={<QrRedirect />} />
            <Route path="/r" element={<QrRedirect />} />
            <Route
              path="/portal"
              element={<PortalEntry />}
            />
            <Route
              path="/portal/campaigns"
              element={
                <Protected>
                  <PortalShell>
                    <PortalCampaigns />
                  </PortalShell>
                </Protected>
              }
            />
            <Route path="/cancel-trial" element={<CancelTrial />} />
            <Route
              path="/portal/success"
              element={
                <Protected>
                  <PortalSuccess />
                </Protected>
              }
            />
            {/* Root-level campaign landing (arcadeops.net/<slug>). MUST stay last before the fallback so fixed routes win. */}
            <Route path="/:slug" element={<CampaignLanding />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
          </Suspense>
          <SupportWidgetHost />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}
